<script lang="ts">
    import cn from 'classnames';
    import { onMount } from 'svelte';
    import { sendRegistrationEvent } from '$lib/services/gtm/methods';
    import { observeOnce } from '$lib/utils';

    import { sendRegistrationStats } from '@api/user/stats';
    import { Accordion } from '@components/common/accordion';
    import { globalStore } from '@components/stores';
    import { EventType } from '@shared/types';
    import { Button, ButtonTypes } from '@uikit/button';

    import type { MainInfoBlockContent } from './types';

    import s from './info-block.module.css';

    export let content: MainInfoBlockContent;
    export let classes = '';

    let sec1El: HTMLElement;
    let showRest: Promise<void>;

    function onClick() {
        sendRegistrationEvent({ event: EventType.MainButtonClickTryForFree });
        sendRegistrationStats(EventType.MainButtonClickTryForFree, null, $globalStore.isDesktopSite);
        window.scrollTo(0, 0);
    }

    onMount(() => {
        showRest = observeOnce(sec1El, {
            rootMargin: '0px 0px -20% 0px',
        });
    });
</script>

<div class={cn(s.container, classes)}>
    <section class={cn(s.sec, s.sec1)} bind:this={sec1El}>
        <div class={s.row}>
            <div class={s.col}>
                <h2 class={s.secTitle}>{content.sec1.title}</h2>
                <p class={s.secText}>{content.sec1.text}</p>
            </div>
            <div class={cn(s.col, s.colImg)}>
                <img
                    loading="lazy"
                    src={content.sec1.imgSrc}
                    alt={content.sec1.imgAlt}
                    class={s.img}
                >
            </div>
        </div>
    </section>

    {#await showRest then}
        <section class={cn(s.sec, s.sec2)}>
            <h2 class={s.secTitle}>{content.sec2.title}</h2>
            <div class={s.secContent}>
                <article class={cn(s.article, s.row, content.sec2.articles[0].classes)}>
                    <div class={cn(s.col, s.colImg)}>
                        <img
                            loading="lazy"
                            src={content.sec2.articles[0].imgSrc}
                            alt={content.sec2.articles[0].imgAlt}
                            class={s.img}
                        >
                    </div>
                    <div class={s.col}>
                        <h3 class={s.articleTitle}>{content.sec2.articles[0].title}</h3>
                        <p class={s.articleText}>{content.sec2.articles[0].text}</p>
                    </div>
                </article>
                <article class={cn(s.article, s.row)}>
                    <div class={s.col}>
                        <h3 class={s.articleTitle}>{content.sec2.articles[1].title}</h3>
                        <p class={s.articleText}>{content.sec2.articles[1].text}</p>
                    </div>
                    <div class={cn(s.col, s.colImg)}>
                        <img
                            loading="lazy"
                            src={content.sec2.articles[1].imgSrc}
                            alt={content.sec2.articles[1].imgAlt}
                            class={s.img}
                        >
                    </div>
                </article>
                <article class={cn(s.article, s.row)}>
                    <div class={cn(s.col, s.colImg)}>
                        <img
                            loading="lazy"
                            src={content.sec2.articles[2].imgSrc}
                            alt={content.sec2.articles[2].imgAlt}
                            class={s.img}
                        >
                    </div>
                    <div class={s.col}>
                        <h3 class={s.articleTitle}>{content.sec2.articles[2].title}</h3>
                        <p class={s.articleText}>{content.sec2.articles[2].text}</p>
                    </div>
                </article>
                <article class={cn(s.article, s.row)}>
                    <div class={s.col}>
                        <h3 class={s.articleTitle}>{content.sec2.articles[3].title}</h3>
                        <p class={s.articleText}>{content.sec2.articles[3].text}</p>
                    </div>
                    <div class={cn(s.col, s.colImg)}>
                        <img
                            loading="lazy"
                            src={content.sec2.articles[3].imgSrc}
                            alt={content.sec2.articles[3].imgAlt}
                            class={s.img}
                        >
                    </div>
                </article>
            </div>
        </section>

        <section class={cn(s.sec, s.sec3)}>
            <h2 class={s.secTitle}>{content.sec3.title}</h2>
            <div class={s.secContent}>
                <article class={cn(s.article, s.row)}>
                    <div class={cn(s.col, s.colImg)}>
                        <img
                            loading="lazy"
                            src={content.sec3.articles[0].imgSrc}
                            alt={content.sec3.articles[0].imgAlt}
                            class={s.img}
                        >
                    </div>
                    <div class={s.col}>
                        <h3 class={s.articleTitle}>{content.sec3.articles[0].title}</h3>
                        <p class={s.articleText}>{content.sec3.articles[0].text}</p>
                    </div>
                </article>
                <article class={cn(s.article, s.row)}>
                    <div class={cn(s.col, s.colImg)}>
                        <img
                            loading="lazy"
                            src={content.sec3.articles[1].imgSrc}
                            alt={content.sec3.articles[1].imgAlt}
                            class={s.img}
                        >
                    </div>
                    <div class={s.col}>
                        <h3 class={s.articleTitle}>{content.sec3.articles[1].title}</h3>
                        <p class={s.articleText}>{content.sec3.articles[1].text}</p>
                    </div>
                </article>
                <article class={cn(s.article, s.row)}>
                    <div class={cn(s.col, s.colImg)}>
                        <img
                            loading="lazy"
                            src={content.sec3.articles[2].imgSrc}
                            alt={content.sec3.articles[2].imgAlt}
                            class={s.img}
                        >
                    </div>
                    <div class={s.col}>
                        <h3 class={s.articleTitle}>{content.sec3.articles[2].title}</h3>
                        <p class={s.articleText}>{content.sec3.articles[2].text}</p>
                    </div>
                </article>
            </div>
        </section>

        <section class={cn(s.sec, s.sec4)}>
            <h2 class={s.secTitle}>{content.sec4.title}</h2>
            <p class={s.secSubtitle}>{content.sec4.text}</p>
            <div class={cn(s.secContent, s.row)}>
                <div class={cn(s.article, s.row)}>
                    <div class={cn(s.col, s.colImg)}>
                        <img
                            loading="lazy"
                            src={content.sec4.articles[0].imgSrc}
                            alt={content.sec4.articles[0].imgAlt}
                            class={s.img}
                        >
                    </div>
                    <p class={s.col}>
                        <span class={s.articleTitle}>{content.sec4.articles[0].title}</span>
                        <span>{content.sec4.articles[0].text}</span>
                    </p>
                </div>
                <div class={cn(s.article, s.row)}>
                    <div class={cn(s.col, s.colImg)}>
                        <img
                            loading="lazy"
                            src={content.sec4.articles[1].imgSrc}
                            alt={content.sec4.articles[1].imgAlt}
                            class={s.img}
                        >
                    </div>
                    <p class={s.col}>
                        <span class={s.articleTitle}>{content.sec4.articles[1].title}</span>
                        <span>{content.sec4.articles[1].text}</span>
                    </p>
                </div>
                <div class={cn(s.article, s.row)}>
                    <div class={cn(s.col, s.colImg)}>
                        <img
                            loading="lazy"
                            src={content.sec4.articles[2].imgSrc}
                            alt={content.sec4.articles[2].imgAlt}
                            class={s.img}
                        >
                    </div>
                    <p class={s.col}>
                        <span class={s.articleTitle}>{content.sec4.articles[2].title}</span>
                        <span>{content.sec4.articles[2].text}</span>
                    </p>
                </div>
            </div>
            <Button
                text={content.sec4.buttonLabel}
                color={ButtonTypes.Color.Brand}
                classes={s.button}
                on:click={onClick}
                attrs={{ 'data-testid': 'tryForFreeButton' }}
            />
        </section>

        <section class={cn(s.sec, s.sec5)}>
        <h2 class={s.secTitle}>{content.sec5.title}</h2>
        <Accordion
            closeButtonClasses={s.closeButton}
            titleClasses={s.questionTitle}
            textClasses={s.questionText}
            items={content.sec5.questions}
            let:item
        >
            <div class={s.questionTextInner}>{item.text}</div>
        </Accordion>
    </section>
    {/await}
</div>
